import {
  HOTEL_DETAILS_ACTIVE_TAB,
  MODAL
} from "@website/accommodation-ui/constant";
import { HotelCardThumbnailPropertiesType } from "@website/accommodation-ui/hotel-card-new/hotel-card-thumbnail/hotel-card-thumbnail.types";
import styles from "@website/accommodation-ui/hotel-card-new/hotel-card-thumbnail/style.module.scss";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { CarouselV2 } from "@website/ui/carousel";
import { Chips } from "@website/ui/chips";
import { LazyImage } from "@website/ui/lazy-image";
import { MiniChips } from "@website/ui/mini-chips";
import { starsHotelImage } from "@website/ui/stars-hotel-image";
import { Tooltip } from "@website/ui/tooltip";
import { deepCompare } from "@website/utils/deep-compare";
import { getImage } from "@website/utils/get-image";
import { numberConvert } from "@website/utils/number-convert";
import { useRouter } from "next/router";
import { FC, ReactElement, memo } from "react";

const ThumbnailWrapper: FC<{
  children: ReactElement;
  size: HotelCardThumbnailPropertiesType["size"];
}> = ({ children, size }) =>
  size === "xSmall" ? <div className="">{children}</div> : children;

const Component: FC<HotelCardThumbnailPropertiesType> = (properties) => {
  const {
    id,
    specialOffer,
    primaryBadge,
    size,
    title,
    stars = 1,
    gallery,
    thumbnail,
    showCarousel
  } = properties;
  const router = useRouter();

  return (
    <div
      className={`${styles.card} relative flex h-[192px] shrink-0 md:h-auto ${
        size === "small" ? "w-full" : ""
      } ${size === "xSmall" ? "!size-24 !h-auto !p-0" : ""} ${
        size !== "small" && size !== "xSmall" ? "md:w-[306px] lg:w-[312px]" : ""
      } ${size === "medium" ? "!w-[177px]" : ""} px-3 pt-3 sm:p-0`}
    >
      {primaryBadge?.text && size !== "xSmall" && (
        <Tooltip
          className="z-[1] cursor-pointer"
          title={`${numberConvert(primaryBadge?.description, {
            locale: router.locale
          })}`}
          titleClassName="hidden !max-w-[250px] md:block !transform-none !left-0 right-0 !top-7"
        >
          <Chips
            EndIcon={
              <MaterialIcon
                className="text-headline-6"
                name="info"
                variant="outlined"
              />
            }
            StartIcon={
              primaryBadge.icon ? (
                <MaterialIcon
                  className="text-xl"
                  name={
                    primaryBadge.color === "#FFAF00"
                      ? "star"
                      : primaryBadge.icon
                  }
                  variant="fill"
                />
              ) : undefined
            }
            className={`!text-on-surface-high-emphasis !absolute inset-x-3 top-0 !h-[28px] whitespace-nowrap ${
              primaryBadge.color === "#FFAF00"
                ? "bg-[linear-gradient(180deg,_#FFDF38_0%,_#FFAF00_100%)]"
                : "!bg-white"
            }`}
            color={primaryBadge.color}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              return router.replace(
                {
                  pathname: router?.pathname,
                  query: {
                    ...router?.query,
                    modal: `badge-info-${id}`
                  }
                },
                undefined,
                { shallow: true, scroll: false }
              );
            }}
            text={numberConvert(primaryBadge.text, { locale: router.locale })}
          />
        </Tooltip>
      )}
      <ThumbnailWrapper size={size}>
        {gallery && showCarousel && (gallery?.length ?? 0) > 1 ? (
          <CarouselV2
            disableArrowKeys
            hasDots={gallery && gallery?.length > 1}
            hasNavigation={
              gallery && gallery?.length > 1
                ? ("fixed" as unknown as boolean)
                : false
            }
            keyCarousel="hotel-card-thumbnail"
            leftBtnClassName="rounded-lg left-2"
            list={[
              ...(gallery?.length >= 5
                ? gallery?.slice(0, 5) || []
                : gallery || []
              ).map((img) => (
                <LazyImage
                  alt={img?.title || "تصاویر هتل"}
                  className={`h-[192px] rounded-lg object-cover lg:h-[216px] ${
                    size === "xSmall"
                      ? "size-24"
                      : "min-h-full w-full lg:h-[234px]"
                  }`}
                  fallback={starsHotelImage(stars).src}
                  key={img?.media}
                  loading="lazy"
                  src={getImage({
                    width: size === "xSmall" ? 200 : 350,
                    height: size === "xSmall" ? 200 : 350,
                    src: img?.media
                  })}
                  title={title}
                />
              )),
              <div
                className="relative size-full"
                key="load_gallery"
                onClick={(event) => {
                  event?.preventDefault();
                  event?.stopPropagation();
                  return router[
                    router.pathname.includes("map") ? "replace" : "push"
                  ](
                    {
                      pathname: router?.pathname,
                      query: {
                        ...router.query,
                        selectedHotelId: id,
                        [process.env.NEXT_PUBLIC_APP_NAME === "tour"
                          ? HOTEL_DETAILS_ACTIVE_TAB
                          : MODAL]: "gallery"
                      }
                    },
                    undefined,
                    { shallow: true, scroll: false }
                  );
                }}
              >
                <LazyImage
                  alt={gallery?.[gallery?.length - 1]?.title || "تصاویر هتل"}
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  className={`hotel-card-image h-[192px] rounded-lg object-cover blur-sm lg:h-[216px] ${
                    size === "xSmall"
                      ? "size-24"
                      : "min-h-full w-full lg:h-[234px]"
                  }`}
                  fallback={starsHotelImage(stars).src}
                  key={gallery?.[gallery?.length - 1]?.media}
                  loading="lazy"
                  src={getImage({
                    width: size === "xSmall" ? 200 : 350,
                    height: size === "xSmall" ? 200 : 350,
                    src: gallery?.[gallery?.length - 1]?.media
                  })}
                  title={title}
                />
                <Button
                  StartIcon={
                    <MaterialIcon
                      className="bg-dim-background text-on-surface-high-emphasis"
                      name="collections"
                      variant="fill"
                    />
                  }
                  className="text-caption !bg-dim-background !text-on-surface-high-emphasis !absolute inset-x-0 inset-y-1/2 z-10 m-auto w-[155px] !rounded-2xl"
                  size="large"
                  text="عکس‌های بیشتر"
                />
              </div>
            ]}
            mode="free"
            options={{
              slides: {
                spacing: 16
              }
            }}
            rightBtnClassName="rounded-lg right-2"
            sectionClassName="rounded-lg"
          />
        ) : (
          <LazyImage
            alt={thumbnail?.alt || "تصاویر هتل"}
            className={`rounded-lg object-cover lg:h-[216px] ${
              size === "small" && "h-[160px] lg:h-[200px]"
            } ${
              size === "xSmall"
                ? "!size-[96px] !rounded"
                : "min-h-full w-full lg:h-[234px]"
            }`}
            fallback={starsHotelImage(stars).src}
            loading="lazy"
            src={getImage({
              width: size === "xSmall" ? 200 : 350,
              height: size === "xSmall" ? 200 : 350,
              src: thumbnail?.url
            })}
            title={title}
          />
        )}
      </ThumbnailWrapper>
      {specialOffer && (
        <div className="!absolute inset-x-3 top-3 whitespace-nowrap">
          <MiniChips
            color="default"
            text={specialOffer}
            variant="round-contained"
          />
        </div>
      )}
    </div>
  );
};

export const HotelCardThumbnail = memo(Component, deepCompare);
